import type { DriverState, MiniSectorState, RaceSnapshot, SessionCapabilities } from './race';
import { displayValue, unsupportedValue, type DisplayValue } from './display-state';

export interface TimingMiniCell {
  state: DisplayValue<MiniSectorState>;
  index: number;
}

export interface TimingSectorCell {
  time: DisplayValue<number>;
  state: DisplayValue<MiniSectorState>;
  lap: number | null;
  minis: TimingMiniCell[];
}

export interface TimingRow {
  driver: DriverState;
  position: DisplayValue<number>;
  interval: DisplayValue<number | string>;
  lap: DisplayValue<number>;
  lastLap: DisplayValue<number>;
  sectors: [TimingSectorCell, TimingSectorCell, TimingSectorCell];
}

function sourceAtMs(value: string | null | undefined, fallback: number | null, sessionStart: string) {
  const parsed = value ? Date.parse(value) : Number.NaN;
  const start = Date.parse(sessionStart);
  return Number.isFinite(parsed) && Number.isFinite(start) ? Math.max(0, parsed - start) : fallback;
}

function lastKnownSector(driver: DriverState, index: number) {
  for (const lap of [...(driver.recentLaps ?? [])].sort((a, b) => b.atMs - a.atMs)) {
    const value = lap.sectors[index];
    if (!lap.deleted && !lap.partial && value !== null && Number.isFinite(value) && value > 0) return { value, lap: lap.lap, atMs: lap.atMs };
  }
  return null;
}

function sectorCell(driver: DriverState, index: number, snapshot: RaceSnapshot, capabilities?: SessionCapabilities): TimingSectorCell {
  const detail = driver.sectorDetails?.[index];
  const fallback = lastKnownSector(driver, index);
  const value = driver.sectors[index] ?? fallback?.value ?? null;
  const lap = detail?.lap ?? fallback?.lap ?? null;
  const observedAtMs = sourceAtMs(detail?.updatedAt, fallback?.atMs ?? null, snapshot.session.dateStart);
  const time = capabilities?.sectorTimes === 'unsupported'
    ? unsupportedValue<number>('sectorTimes', capabilities)
    : displayValue(value, observedAtMs, snapshot.atMs, { lap });
  const state = displayValue(detail?.state ?? null, observedAtMs, snapshot.atMs, { lap });
  const minis = capabilities?.miniSectors === 'unsupported'
    ? []
    : (driver.miniSectors?.[index] ?? []).map((state, mini) => {
      // A mini-sector is its own observation. Its lap and source time may
      // differ from the enclosing sector (especially across a lap boundary),
      // so never inherit the parent sector metadata when segment metadata is
      // available. Missing metadata remains an unscoped observation.
      const segment = detail?.segments?.[mini];
      const segmentAtMs = sourceAtMs(segment?.updatedAt, null, snapshot.session.dateStart);
      return { index: mini, state: displayValue(state === 'unknown' ? null : state, segmentAtMs, snapshot.atMs, { lap: segment?.lap ?? null }) };
    });
  return { time, state, lap, minis };
}

export function projectTimingRows(snapshot: RaceSnapshot, capabilities: SessionCapabilities = {
  miniSectors: 'unknown', sectorTimes: 'unknown', carTelemetry: 'unknown', location: 'unknown'
}): TimingRow[] {
  return [...snapshot.drivers].sort((a, b) => (a.position ?? 999) - (b.position ?? 999) || a.number - b.number).map(driver => {
    const updatedAtMs = sourceAtMs(driver.timingUpdatedAt, null, snapshot.session.dateStart);
    const intervalValue = driver.position === 1 ? driver.gapToLeader : driver.interval;
    const sectors = [0, 1, 2].map(index => sectorCell(driver, index, snapshot, capabilities)) as [TimingSectorCell, TimingSectorCell, TimingSectorCell];
    const lap = displayValue(driver.lap > 0 ? driver.lap : null, updatedAtMs, snapshot.atMs, { lap: driver.lap > 0 ? driver.lap : null });
    const lastLap = displayValue(driver.lastLap, updatedAtMs, snapshot.atMs, { lap: driver.completedLaps ?? null });
    // TimingData heartbeats only prove the timing channel is alive. They do
    // not refresh an unchanged interval; interval freshness must use the
    // interval field's own source timestamp.
    const interval = displayValue(intervalValue, sourceAtMs(driver.intervalUpdatedAt, null, snapshot.session.dateStart), snapshot.atMs, { lap: driver.lap });
    const position = displayValue(driver.position, updatedAtMs, snapshot.atMs, { lap: driver.lap });
    return { driver, position, interval, lap, lastLap, sectors };
  });
}
